import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { SlIcon } from '@shoelace-style/shoelace/dist/react';
import { Toast } from '../../../../shared/Toast';
import { uploadImage, uploadVideo } from '../api';
import { isVideoSubType, subTypeLabel } from '../config';
import ConfirmDialog from './ConfirmDialog';

const IMAGE_ACCEPT = { 'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'] };
const VIDEO_ACCEPT = { 'video/*': ['.mp4', '.mov', '.webm', '.avi', '.m4v'] };

const prettySize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** One drop slot with its own preview, progress bar and clear button. */
const DropSlot = ({
  title,
  hint,
  accept,
  kind,
  file,
  previewUrl,
  onFile,
  onClear,
  progress,
  done,
  busy,
}) => {
  const onDrop = useCallback(
    (accepted, rejected) => {
      if (rejected?.length) {
        Toast('error', `That file type is not allowed here — ${hint}`);
        return;
      }
      if (accepted?.length) onFile(accepted[0]);
    },
    [onFile, hint]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept,
    multiple: false,
    disabled: busy,
  });

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SlIcon
            library="lucide"
            name={kind === 'video' ? 'video' : 'image'}
            className="text-slate-400"
            style={{ fontSize: '15px' }}
          />
          <span className="text-sm font-semibold text-slate-800">{title}</span>
          {done && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-[2px] text-[10px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
              <SlIcon library="lucide" name="check" style={{ fontSize: '10px' }} />
              Uploaded
            </span>
          )}
        </div>
        {file && !busy && !done && (
          <button
            type="button"
            onClick={onClear}
            className="text-xs font-medium text-slate-400 transition hover:text-rose-600"
          >
            Remove
          </button>
        )}
      </div>

      {!file ? (
        <div
          {...getRootProps()}
          tabIndex={0}
          className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-7 text-center transition focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 ${
            isDragReject
              ? 'border-rose-300 bg-rose-50'
              : isDragActive
                ? 'border-sky-400 bg-sky-50'
                : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'
          } ${busy ? 'pointer-events-none opacity-60' : ''}`}
        >
          <input {...getInputProps()} />
          <SlIcon
            library="lucide"
            name="cloud-upload"
            className="mb-2 text-slate-400"
            style={{ fontSize: '22px' }}
          />
          <p className="text-sm font-medium text-slate-700">Drop a file, or click to browse</p>
          <p className="mt-0.5 text-xs text-slate-400">{hint} · paste with Ctrl/⌘+V</p>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
            {kind === 'video' ? (
              <video src={previewUrl} controls className="max-h-52 w-full bg-black" />
            ) : (
              <img
                src={previewUrl}
                alt={`${title} preview`}
                className="max-h-52 w-full object-contain"
              />
            )}
          </div>
          <div className="flex items-center justify-between text-xs text-slate-500">
            {/* NOT `truncate`: Styles.scss:3997 adds `max-width: 130px`.
                `min-w-0` lets the flex item shrink so `max-w-[70%]` applies. */}
            <span
              className="min-w-0 max-w-[70%] overflow-hidden text-ellipsis whitespace-nowrap"
              title={file.name}
            >
              {file.name}
            </span>
            <span>{prettySize(file.size)}</span>
          </div>
          {(busy || done) && (
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
              <div
                className={`h-full rounded-full transition-all duration-200 ${
                  done ? 'bg-emerald-500' : 'bg-sky-600'
                }`}
                style={{ width: `${done ? 100 : progress}%` }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Upload modal. For video sub-types both a thumbnail and a video are required;
 * the server decides which upload finalises the design, so the client just
 * sends the image leg first and the video leg second.
 */
const UploadDesignModal = ({ open, row, onClose, onUploaded }) => {
  const video = isVideoSubType(row?.sub_type);

  const [imageFile, setImageFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [videoPreview, setVideoPreview] = useState('');
  const [imageProgress, setImageProgress] = useState(0);
  const [videoProgress, setVideoProgress] = useState(0);
  const [imageDone, setImageDone] = useState(false);
  // Persists the thumbnail URL across a retry that only re-runs the video leg (imageDone
  // already true on retry, so the image-upload branch in handleUpload is skipped and can't
  // re-derive it locally) — needed so the video call can still fold it into one combined entry.
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const panelRef = useRef(null);
  const restoreFocusRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Object URLs, revoked on change/unmount.
  useEffect(() => {
    if (!imageFile) {
      setImagePreview('');
      return undefined;
    }
    const url = URL.createObjectURL(imageFile);
    setImagePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  useEffect(() => {
    if (!videoFile) {
      setVideoPreview('');
      return undefined;
    }
    const url = URL.createObjectURL(videoFile);
    setVideoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [videoFile]);

  // Reset whenever a different creative is opened.
  useEffect(() => {
    if (!open) return;
    setImageFile(null);
    setVideoFile(null);
    setImageProgress(0);
    setVideoProgress(0);
    setImageDone(false);
    setThumbnailUrl('');
    setError(null);
    setConfirmOpen(false);
  }, [open, row?.bsc_id]);

  // Body + outlet scroll lock, focus capture/restore.
  useEffect(() => {
    if (!open) return undefined;
    restoreFocusRef.current = document.activeElement;
    const body = document.body;
    const outlet = document.querySelector('.outlet-body');
    const prevBody = body.style.overflow;
    const prevOutlet = outlet ? outlet.style.overflow : '';
    body.style.overflow = 'hidden';
    if (outlet) outlet.style.overflow = 'hidden';
    const focusTimer = setTimeout(() => panelRef.current?.focus(), 0);

    return () => {
      clearTimeout(focusTimer);
      body.style.overflow = prevBody;
      if (outlet) outlet.style.overflow = prevOutlet;
      const el = restoreFocusRef.current;
      if (el && typeof el.focus === 'function') el.focus();
    };
  }, [open]);

  const uploadingRef = useRef(uploading);
  uploadingRef.current = uploading;
  const confirmOpenRef = useRef(confirmOpen);
  confirmOpenRef.current = confirmOpen;

  const requestClose = useCallback(() => {
    if (uploadingRef.current) return;
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;
    // Capture phase: this also stops Esc from reaching the DetailDrawer's
    // bubble-phase listener behind us. While the confirm dialog is open we
    // bow out entirely (no stopPropagation) so its own capture listener —
    // registered later, therefore called later in the capture phase — gets
    // the key and cancels the dialog instead of closing this modal.
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (confirmOpenRef.current) return;
      e.stopPropagation();
      requestClose();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, requestClose]);

  // Clipboard paste routes to the matching slot.
  useEffect(() => {
    if (!open) return undefined;
    const onPaste = (e) => {
      if (uploadingRef.current || confirmOpenRef.current) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.kind !== 'file') continue;
        const original = item.getAsFile();
        if (!original) continue;
        const isImg = original.type.startsWith('image/');
        const isVid = original.type.startsWith('video/');
        if (!isImg && !isVid) continue;
        const ext = original.type.split('/')[1] || 'bin';
        const file = new File([original], `clipboard-${Date.now()}.${ext}`, {
          type: original.type,
        });
        if (isImg) setImageFile(file);
        else if (video) setVideoFile(file);
        else {
          Toast('error', 'This creative expects an image, not a video.');
          return;
        }
        e.preventDefault();
        return;
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [open, video]);

  if (!open || !row) return null;

  const ready = video ? (imageDone || !!imageFile) && !!videoFile : !!imageFile;

  // The confirmation deliberately fires from *inside* the modal, after the
  // files are chosen, so the user can see exactly what is being submitted.
  const requestUpload = () => {
    if (!ready || uploading) return;
    setConfirmOpen(true);
  };

  const confirmFiles = [
    imageFile && {
      key: 'image',
      icon: 'image',
      label: video ? 'Thumbnail' : 'Design image',
      file: imageFile,
    },
    video && videoFile && { key: 'video', icon: 'video', label: 'Video', file: videoFile },
  ].filter(Boolean);

  const handleUpload = async () => {
    if (!ready || uploading) return;
    setUploading(true);
    setError(null);

    const collectedLogs = [];
    let last = null;
    let uploadedImgUrl;
    let uploadedVideoUrl;
    // Local mirror of `thumbnailUrl` state — read fresh here in case this is the first upload
    // (state hasn't re-rendered yet within this same async call).
    let thumbnailUrlForVideo = thumbnailUrl;

    try {
      if (!imageDone) {
        setImageProgress(0);
        const res = await uploadImage({
          bsc_id: row.bsc_id,
          file: imageFile,
          // For video sub-types the thumbnail is one half of a single design version — its log
          // write is suppressed here and folded into the video call's combined entry below via
          // thumbnail_url, so uploading a thumbnail+video pair produces exactly one version.
          skip_log: video,
          onUploadProgress: (e) => {
            if (!e?.total || !mountedRef.current) return;
            setImageProgress(Math.min(99, Math.round((e.loaded * 100) / e.total)));
          },
        });
        if (!mountedRef.current) return;
        collectedLogs.push(...(res?.logs || []));
        last = res;
        uploadedImgUrl = res?.url;
        thumbnailUrlForVideo = res?.url || '';
        setThumbnailUrl(thumbnailUrlForVideo);
        setImageProgress(100);
        setImageDone(true);
      }

      if (video) {
        setVideoProgress(0);
        const res = await uploadVideo({
          bsc_id: row.bsc_id,
          file: videoFile,
          thumbnail_url: thumbnailUrlForVideo,
          onUploadProgress: (e) => {
            if (!e?.total || !mountedRef.current) return;
            setVideoProgress(Math.min(99, Math.round((e.loaded * 100) / e.total)));
          },
        });
        if (!mountedRef.current) return;
        collectedLogs.push(...(res?.logs || []));
        last = res;
        uploadedVideoUrl = res?.url;
        setVideoProgress(100);
      }

      Toast('success', 'Design uploaded successfully');
      onUploaded({
        bsc_id: row.bsc_id,
        status: last?.status ?? row.status,
        imgUrl: uploadedImgUrl,
        videoUrl: uploadedVideoUrl,
        logs: collectedLogs,
      });
      if (mountedRef.current) setUploading(false);
      onClose();
      return;
    } catch (err) {
      if (!mountedRef.current) return;
      const message = err?.message || 'Upload failed. Please try again.';
      setError(message);
      Toast('error', message);
      // Anything already pushed to the server stays applied to the thread.
      if (collectedLogs.length) {
        onUploaded({
          bsc_id: row.bsc_id,
          status: last?.status ?? row.status,
          imgUrl: uploadedImgUrl,
          videoUrl: uploadedVideoUrl,
          logs: collectedLogs,
          partial: true,
        });
      }
    }
    if (mountedRef.current) setUploading(false);
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-slate-900/50 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) requestClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Upload design"
        tabIndex={-1}
        className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl outline-none sm:rounded-2xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-slate-900">Upload design</h2>
            {/* NOT `truncate`: Styles.scss:3997 adds `max-width: 130px`. */}
            <p className="mt-0.5 overflow-hidden text-ellipsis whitespace-nowrap text-xs text-slate-500">
              {row.creative_name || `Creative #${row.bsc_id}`} · {subTypeLabel(row.sub_type)}
            </p>
          </div>
          <button
            type="button"
            onClick={requestClose}
            disabled={uploading}
            aria-label="Close"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40"
          >
            <SlIcon library="lucide" name="x" style={{ fontSize: '18px' }} />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-slate-50 p-4">
          <p className="flex items-start gap-1.5 text-xs text-slate-500">
            <SlIcon
              library="lucide"
              name="info"
              className="mt-[1px] shrink-0"
              style={{ fontSize: '13px' }}
            />
            <span>
              This is your deliverable. To share a reference file instead, attach it in the Activity
              view.
            </span>
          </p>

          {video && (
            <p className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
              This sub-type needs both a thumbnail image and the video. The design is submitted for
              review once the video finishes uploading.
            </p>
          )}

          <DropSlot
            title={video ? 'Thumbnail' : 'Design image'}
            hint="JPG, PNG, GIF or WEBP"
            accept={IMAGE_ACCEPT}
            kind="image"
            file={imageFile}
            previewUrl={imagePreview}
            onFile={setImageFile}
            onClear={() => {
              setImageFile(null);
              setImageProgress(0);
            }}
            progress={imageProgress}
            done={imageDone}
            busy={uploading}
          />

          {video && (
            <DropSlot
              title="Video"
              hint="MP4, MOV, WEBM or AVI"
              accept={VIDEO_ACCEPT}
              kind="video"
              file={videoFile}
              previewUrl={videoPreview}
              onFile={setVideoFile}
              onClear={() => {
                setVideoFile(null);
                setVideoProgress(0);
              }}
              progress={videoProgress}
              done={false}
              busy={uploading}
            />
          )}

          {error && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <button
            type="button"
            onClick={requestClose}
            disabled={uploading}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={requestUpload}
            disabled={!ready || uploading}
            className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {uploading && (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-sky-300 border-t-white" />
            )}
            {uploading ? 'Uploading…' : error ? 'Retry upload' : 'Upload design'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Submit this design?"
        message={
          Number(row.status) === 4
            ? 'This replaces the design you previously submitted and sends the new one for review.'
            : 'This replaces the current design file and submits it for review.'
        }
        confirmLabel="Upload & submit"
        cancelLabel="Keep editing"
        tone="primary"
        busy={false}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          // Close first: the modal underneath owns the progress bars and the
          // "Uploading…" button, and a mounted dialog would cover both.
          setConfirmOpen(false);
          handleUpload();
        }}
      >
        <ul className="space-y-1.5">
          {confirmFiles.map((entry) => (
            <li
              key={entry.key}
              className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2"
            >
              <SlIcon
                library="lucide"
                name={entry.icon}
                className="shrink-0 text-slate-400"
                style={{ fontSize: '14px' }}
              />
              <span className="shrink-0 text-xs font-semibold text-slate-700">{entry.label}</span>
              {/* NOT `truncate`: Styles.scss:3997 adds `max-width: 130px`. */}
              <span
                className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-xs text-slate-500"
                title={entry.file.name}
              >
                {entry.file.name}
              </span>
              <span className="shrink-0 text-xs text-slate-400">{prettySize(entry.file.size)}</span>
            </li>
          ))}
        </ul>
        {video && (
          <p className="mt-2 text-xs text-slate-500">
            The submission is finalised once the video finishes uploading.
          </p>
        )}
      </ConfirmDialog>
    </div>
  );
};

export default UploadDesignModal;
